import { useCallback, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLogCaptureAvailable } from '../../../components/admin/useLogCaptureAvailable';
import { ChatOAuthDialogState } from './settingsTypes';
import { useChatNotificationPreferences } from './useChatNotificationPreferences';
import { useChatOAuthReturn } from './useChatOAuthReturn';
import { useNotificationSubscriptions } from './useNotificationSubscriptions';

export function useAdminSettingsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const chatAlertsSectionRef = useRef<HTMLDivElement>(null);
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'cross-device' ? 1 : tabParam === 'support' ? 2 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loadError, setLoadErrorState] = useState<string | null>(null);
  const [chatOAuthDialog, setChatOAuthDialog] = useState<ChatOAuthDialogState | null>(null);

  const onLoadError = useCallback((message: string) => {
    setLoadErrorState(message || null);
  }, []);

  const onSaveSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
  }, []);

  const subscriptions = useNotificationSubscriptions({ onLoadError, onSaveSuccess });
  const chat = useChatNotificationPreferences({ onLoadError, onSaveSuccess });

  useChatOAuthReturn({
    searchParams,
    navigate,
    chatAlertsSectionRef,
    setActiveTab,
    setChatPrefs: chat.setChatPrefs,
    fetchChatNotificationPreferences: chat.fetchChatNotificationPreferences,
    setChatOAuthDialog,
  });

  // The Support tab (2) exists only on deployments with backend CLIENT_LOG_CAPTURE_ENABLED; a
  // ?tab=support link elsewhere falls back to the first tab.
  const supportAvailable = useLogCaptureAvailable();
  const tab = activeTab === 2 && !supportAvailable ? 0 : activeTab;

  return {
    activeTab: tab,
    supportAvailable,
    setActiveTab,
    successMessage,
    setSuccessMessage,
    loadError,
    setLoadError: setLoadErrorState,
    chatOAuthDialog,
    setChatOAuthDialog,
    chatAlertsSectionRef,
    subscriptions,
    chat,
  };
}
